import { useQuery } from '@tanstack/react-query';
import Image from 'next/image';
import { ReactNode, useEffect, useMemo, useState } from 'react';

import { getAssetsPlayersImages, getSimpleFooter } from 'services/idm';

import { PUBLIC_GTM_ID_ENV } from 'tools/environments';

import { CMSPicture } from 'core/generic/elements/cms-picture';

import { getClubImagePath } from '@login/lib/club';

import { PageTemplate } from '../patterns';
import styles from './layout.module.scss';

type LayoutProps = {
	children: ReactNode;
	backgroundUrl?: string;
	locale?: string;
};

export const Layout = ({
	children,
	backgroundUrl = '',
	locale = 'en',
}: LayoutProps) => {
	const { data: playerImages } = useQuery({
		queryKey: ['login-player-images', locale],
		queryFn: () => getAssetsPlayersImages(locale),
	});

	const { data: footerData } = useQuery({
		queryKey: ['simpler-footer', locale],
		queryFn: () => getSimpleFooter(locale),
	});

	// Picked after mount rather than during render: a random choice made while
	// rendering differs between server and client and would not survive
	// hydration, and it changed on every re-render.
	const [playerIndex, setPlayerIndex] = useState<number | null>(null);

	useEffect(() => {
		if (!playerImages?.length) return;
		setPlayerIndex(Math.floor(Math.random() * playerImages.length));
	}, [playerImages]);

	const playerImage =
		playerIndex === null ? undefined : playerImages?.[playerIndex];

	const backgroundImage = useMemo(
		() => backgroundUrl || getClubImagePath('signin-background.webp'),
		[backgroundUrl]
	);

	const config = useMemo(() => ({ gtmId: PUBLIC_GTM_ID_ENV }), []);

	return (
		<PageTemplate footerData={footerData} config={config}>
			<div className={styles.backgroundWrapper}>
				<div className={styles.imageContainer}>
					<Image
						src={backgroundImage}
						alt=""
						className={styles.background}
						fill
						priority
					/>

					{playerImage && (
						<CMSPicture
							url={playerImage.url || ''}
							alt={playerImage.alt || ''}
							className={styles.player}
							height={359}
							addBackgroundColour={false}
						/>
					)}
				</div>

				<div className="container">
					<div className="row">
						<div className="col-12">
							<div className={styles.container}>
								<div className={styles.formWrapper}>
									{children}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</PageTemplate>
	);
};
